import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, shopApi, getShopSession } from "@/lib/api";
import { useShopIdleLogout } from "@/hooks/useShopIdleLogout";
import { ShopHeader } from "@/components/ShopHeader";
import { ReportPrintHeader } from "@/components/ReportPrintHeader";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";

/** Print helper — blocks print until a shop logo is uploaded. */
function usePrintGuard(shop, t) {
  return () => {
    if (!shop?.logo_path) {
      toast.error(t("reports.logo_required"));
      return;
    }
    window.print();
  };
}
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ChevronLeft, Printer, Mail, RefreshCw, FileText, TrendingUp, PieChart, User2, Clock, Scale } from "lucide-react";

function fmtTHB(n) { return `฿${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
function fmtTHBn(n) { return `฿${Math.round(Number(n || 0)).toLocaleString("en-US")}`; }

function today() {
  // Bangkok (UTC+7) date: shift UTC epoch forward 7h and read ISO date.
  const bkk = new Date(Date.now() + 420 * 60000);
  return bkk.toISOString().slice(0, 10);
}
function daysAgo(n) {
  const bkk = new Date(Date.now() + 420 * 60000 - n * 86400000);
  return bkk.toISOString().slice(0, 10);
}

/* ---------------- Daily ---------------- */
function DailyReport({ slug, shop, byTime = false }) {
  const { t, lang } = useLang();
  const print = usePrintGuard(shop, t);
  // Default to the shop's current BUSINESS day (overnight-aware) so an
  // operator opening Reports at 02:00 for a 08-03 shop still sees today's
  // takings instead of a blank calendar-today sheet.
  const [date, setDate] = useState(shop?.business_date_today || today());
  useEffect(() => {
    if (shop?.business_date_today) setDate(shop.business_date_today);
    // eslint-disable-next-line
  }, [shop?.business_date_today]);
  const [data, setData] = useState({ rows: [], totals: {} });
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await shopApi(slug).get(`/shops/${slug}/reports/daily`, { params: { date } });
      const rows = byTime
        ? [...r.data.rows].sort((a, b) => a.time.localeCompare(b.time))
        : r.data.rows;
      setData({ ...r.data, rows });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date, byTime]);
  return (
    <>
      <ReportPrintHeader
        shop={shop}
        title={byTime ? t("reports.customers_by_time") : t("reports.daily")}
        range={date}
      />
      <div className="flex items-end gap-3 mb-4 flex-wrap print:hidden">
        <div>
          <Label className="caption-label">{t("common.date")}</Label>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid={byTime ? "customers-by-time-date" : "daily-date"} />
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid={byTime ? "customers-by-time-refresh" : "daily-refresh"}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
        <Button variant="outline" size="sm" onClick={print} data-testid={byTime ? "customers-by-time-print" : "daily-print"}><Printer className="w-3.5 h-3.5 mr-1" />{t("common.print")}</Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={byTime ? "customers-by-time-table" : "daily-worksheet-table"}>
          <thead className="text-left border-b border-stone-200">
            <tr>
              <th className="py-2 pr-3 caption-label">{t("common.working_name")}</th>
              <th className="py-2 pr-3 caption-label">{t("common.time")}</th>
              <th className="py-2 pr-3 caption-label">{t("common.service")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("common.minutes")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("reports.col.price_per_60m")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("common.price")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("common.discount")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("common.commission")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("common.shop_income")}</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(r => (
              <tr key={r.id} className="border-b border-stone-100" data-testid={`daily-row-${r.id}`}>
                <td className="py-2 pr-3">{r.working_name}</td>
                <td className="py-2 pr-3 font-mono">{r.time}</td>
                <td className="py-2 pr-3">{(lang === "th" && r.service_name_th) ? r.service_name_th : r.service}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{r.duration}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHBn(r.price_per_60)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHBn(r.price)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHBn(r.discount)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.commission)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.shop_income)}</td>
              </tr>
            ))}
            {data.rows.length === 0 && !loading && <tr><td colSpan={9} className="py-6 text-center text-stone-500">{t("reports.no_data")}</td></tr>}
          </tbody>
          {data.rows.length > 0 && (
            <tfoot className="font-medium border-t-2 border-stone-300 bg-stone-50/60" data-testid="daily-totals-row">
              <tr>
                <td colSpan={5} className="py-2 pr-3 text-right">{t("common.total")}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHBn(data.totals.income)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHBn(data.totals.discount)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(data.totals.commission)}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(data.totals.shop_income)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </>
  );
}

/* ---------------- Profit ---------------- */
function ProfitReport({ slug, shop, summary = false }) {
  const { t } = useLang();
  const print = usePrintGuard(shop, t);
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(today());
  const [data, setData] = useState({ rows: [], totals: {} });
  const load = async () => {
    try {
      const url = summary ? `/shops/${slug}/reports/summary-profit` : `/shops/${slug}/reports/profit`;
      const r = await shopApi(slug).get(url, { params: { start, end } });
      setData(summary ? { rows: [], totals: r.data.totals } : r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);
  return (
    <>
      <ReportPrintHeader
        shop={shop}
        title={summary ? t("reports.summary_profit") : t("reports.profit")}
        range={`${start} → ${end}`}
      />
      <div className="flex items-end gap-3 mb-4 flex-wrap print:hidden">
        <div>
          <Label className="caption-label">{t("common.start_date")}</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid={summary ? "summary-start" : "profit-start"} />
        </div>
        <div>
          <Label className="caption-label">{t("common.end_date")}</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid={summary ? "summary-end" : "profit-end"} />
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
        <Button variant="outline" size="sm" onClick={print} data-testid={summary ? "summary-print" : "profit-print"}><Printer className="w-3.5 h-3.5 mr-1" />{t("common.print")}</Button>
      </div>
      {summary ? (
        <div className="grid grid-cols-2 md:grid-cols-7 gap-4" data-testid="summary-totals-row">
          <SummaryTile label={t("common.other_income")} val={data.totals.other_income} />
          <SummaryTile label={t("common.income")} val={data.totals.income} />
          <SummaryTile label={t("common.discount")} val={data.totals.discount} />
          <SummaryTile label={t("common.commission")} val={data.totals.commission} />
          <SummaryTile label={t("common.top_up")} val={data.totals.top_up} />
          <SummaryTile label={t("common.expenses")} val={data.totals.expenses} />
          <SummaryTile label={t("common.profit")} val={data.totals.profit} highlight />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="profit-report-table">
            <thead className="text-left border-b border-stone-200">
              <tr>
                <th className="py-2 pr-3 caption-label">{t("common.date")}</th>
                <th className="py-2 pr-3 caption-label text-right">{t("common.other_income")}</th>
                <th className="py-2 pr-3 caption-label text-right">{t("common.income")}</th>
                <th className="py-2 pr-3 caption-label text-right">{t("common.discount")}</th>
                <th className="py-2 pr-3 caption-label text-right">{t("common.commission")}</th>
                <th className="py-2 pr-3 caption-label text-right">{t("common.top_up")}</th>
                <th className="py-2 pr-3 caption-label text-right">{t("common.expenses")}</th>
                <th className="py-2 pr-3 caption-label text-right">{t("common.profit")}</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map(r => (
                <tr key={r.date} className="border-b border-stone-100" data-testid={`profit-row-${r.date}`}>
                  <td className="py-2 pr-3 font-mono">{r.date}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.other_income)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.income)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.discount)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.commission)}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular ${r.top_up > 0 ? "text-amber-900" : "text-stone-400"}`}>{fmtTHB(r.top_up)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.expenses)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.profit)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={8} className="py-6 text-center text-stone-500">{t("reports.no_data")}</td></tr>}
            </tbody>
            {data.rows.length > 0 && (
              <tfoot className="font-medium border-t-2 border-stone-300 bg-stone-50/60" data-testid="profit-totals-row">
                <tr>
                  <td className="py-2 pr-3 text-right">{t("common.total")}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(data.totals.other_income)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(data.totals.income)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(data.totals.discount)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(data.totals.commission)}</td>
                  <td className={`py-2 pr-3 text-right font-mono tabular ${data.totals.top_up > 0 ? "text-amber-900" : "text-stone-400"}`}>{fmtTHB(data.totals.top_up)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(data.totals.expenses)}</td>
                  <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(data.totals.profit)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </>
  );
}

function SummaryTile({ label, val, highlight }) {
  return (
    <div className={`rounded-lg border ${highlight ? "border-emerald-200 bg-emerald-50/60" : "border-stone-200"} p-4`}>
      <div className="caption-label">{label}</div>
      <div className={`font-mono tabular ${highlight ? "text-emerald-900" : "text-stone-900"} text-2xl mt-1`}>{fmtTHB(val)}</div>
    </div>
  );
}

/* ---------------- Income & Expenditure ---------------- */
const OTHER_CAT_LABEL = { girls_release_fee: "Girls release fee" };

function IncomeExpenditureReport({ slug, shop }) {
  const { t, lang } = useLang();
  const print = usePrintGuard(shop, t);
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await shopApi(slug).get(`/shops/${slug}/reports/income-expenditure`, { params: { start, end } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [start, end]);

  return (
    <>
      <ReportPrintHeader
        shop={shop}
        title={t("reports.income_expenditure")}
        range={`${start} → ${end}`}
      />
      <div className="flex items-end gap-3 mb-4 flex-wrap print:hidden">
        <div>
          <Label className="caption-label">{t("common.start_date")}</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="ie-start" />
        </div>
        <div>
          <Label className="caption-label">{t("common.end_date")}</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="ie-end" />
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="ie-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
        <Button variant="outline" size="sm" onClick={print} data-testid="ie-print"><Printer className="w-3.5 h-3.5 mr-1" />{t("common.print")}</Button>
      </div>

      {loading && <div className="py-6 text-center text-stone-500 text-sm">Loading…</div>}
      {!loading && data && (
        <>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Income */}
            <div className="rounded-lg border border-stone-200 p-5" data-testid="ie-income-block">
              <h3 className="font-serif text-xl mb-3">{t("common.income")}</h3>
              <table className="w-full text-sm mb-2">
                <thead>
                  <tr className="text-left border-b border-stone-200">
                    <th className="py-2 caption-label">{t("common.service")}</th>
                    <th className="py-2 caption-label text-right">{t("common.customers")}</th>
                    <th className="py-2 caption-label text-right">{t("common.amount")}</th>
                  </tr>
                </thead>
                <tbody data-testid="ie-income-services">
                  {data.income.by_service.map(s => (
                    <tr key={s.name} className="border-b border-stone-100">
                      <td className="py-2">{(lang === "th" && s.name_th) ? s.name_th : s.name}</td>
                      <td className="py-2 text-right font-mono tabular">{s.customers}</td>
                      <td className="py-2 text-right font-mono tabular">{fmtTHB(s.amount)}</td>
                    </tr>
                  ))}
                  {data.income.by_service.length === 0 && (
                    <tr><td colSpan={3} className="py-3 text-center text-stone-500 text-xs">{t("reports.ie.no_service_income")}</td></tr>
                  )}
                  <tr className="border-t-2 border-stone-300">
                    <td className="py-2 font-medium" colSpan={2}>{t("reports.ie.subtotal_services")}</td>
                    <td className="py-2 text-right font-mono tabular font-medium" data-testid="ie-total-services">{fmtTHB(data.income.total_service)}</td>
                  </tr>
                </tbody>
              </table>

              <h4 className="caption-label mt-4 mb-1">{t("common.other_income")}</h4>
              <table className="w-full text-sm">
                <tbody data-testid="ie-income-other">
                  {data.income.other_by_category.map(o => (
                    <tr key={o.category} className="border-b border-stone-100">
                      <td className="py-2">{OTHER_CAT_LABEL[o.category] || o.category}</td>
                      <td className="py-2 text-right font-mono tabular">{fmtTHB(o.amount)}</td>
                    </tr>
                  ))}
                  {data.income.other_by_category.length === 0 && (
                    <tr><td colSpan={2} className="py-3 text-center text-stone-500 text-xs">{t("reports.ie.no_other_income")}</td></tr>
                  )}
                  <tr className="border-t-2 border-emerald-800">
                    <td className="py-2 font-serif text-base">{t("reports.ie.total_income")}</td>
                    <td className="py-2 text-right font-mono tabular font-serif text-base text-emerald-900" data-testid="ie-total-income">{fmtTHB(data.income.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Expenditure */}
            <div className="rounded-lg border border-stone-200 p-5" data-testid="ie-expenditure-block">
              <h3 className="font-serif text-xl mb-3">{t("reports.ie.expenditure")}</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-stone-200">
                    <th className="py-2 caption-label">{t("common.type")}</th>
                    <th className="py-2 caption-label text-right">{t("common.amount")}</th>
                  </tr>
                </thead>
                <tbody data-testid="ie-expenditure-rows">
                  <tr className="border-b border-stone-100">
                    <td className="py-2">{t("common.commission")}</td>
                    <td className="py-2 text-right font-mono tabular">{fmtTHB(data.expenditure.commission)}</td>
                  </tr>
                  <tr className="border-b border-stone-100">
                    <td className="py-2">{t("common.discount")}</td>
                    <td className="py-2 text-right font-mono tabular">{fmtTHB(data.expenditure.discount)}</td>
                  </tr>
                  {data.expenditure.top_up > 0 && (
                    <tr className="border-b border-stone-100 bg-amber-50/50" data-testid="ie-topup-row">
                      <td className="py-2 text-amber-900">{t("common.top_up")}</td>
                      <td className="py-2 text-right font-mono tabular text-amber-900">{fmtTHB(data.expenditure.top_up)}</td>
                    </tr>
                  )}
                  {data.expenditure.by_type.map(x => (
                    <tr key={x.type} className="border-b border-stone-100">
                      <td className="py-2">{x.type}</td>
                      <td className="py-2 text-right font-mono tabular">{fmtTHB(x.amount)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-stone-800">
                    <td className="py-2 font-serif text-base">{t("reports.ie.total_expenditure")}</td>
                    <td className="py-2 text-right font-mono tabular font-serif text-base" data-testid="ie-total-expenditure">{fmtTHB(data.expenditure.total)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Profit */}
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            <SummaryTile label={t("reports.ie.total_income")} val={data.income.total} />
            <SummaryTile label={t("reports.ie.total_expenditure")} val={data.expenditure.total} />
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50/60 p-4"
              data-testid="ie-profit-tile"
            >
              <div className="caption-label">{t("common.profit")}</div>
              <div className="font-mono tabular text-emerald-900 text-2xl mt-1">{fmtTHB(data.profit)}</div>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/* ---------------- Staff commission ---------------- */
function StaffCommission({ slug, shop }) {
  const { t } = useLang();
  const print = usePrintGuard(shop, t);
  const [staff, setStaff] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [start, setStart] = useState(daysAgo(30));
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const session = getShopSession(slug);

  useEffect(() => {
    (async () => {
      try {
        const r = await shopApi(slug).get(`/shops/${slug}/staff`);
        setStaff(r.data.staff);
        if (r.data.staff[0]) setStaffId(r.data.staff[0].id);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to load staff");
      }
    })();
  }, [slug]);

  const load = async () => {
    if (!staffId) return;
    try {
      const r = await shopApi(slug).get(`/shops/${slug}/reports/staff-commission`, { params: { start, end, staff_id: staffId } });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [staffId, start, end]);

  const emailReport = async () => {
    if (!data?.staff?.email) return toast.error(t("reports.no_email_on_file"));
    setBusy(true);
    try {
      await shopApi(slug).post(`/shops/${slug}/reports/staff-commission/email`, { staff_id: staffId, start, end });
      toast.success(t("reports.emailed"));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const canEmail = ["owner","manager"].includes(session?.role || "");

  return (
    <>
      <ReportPrintHeader
        shop={shop}
        title={`${t("reports.staff_commission")} — ${data?.staff?.working_name || ""}`.trim()}
        range={`${start} → ${end}`}
      />
      <div className="flex items-end gap-3 mb-4 flex-wrap print:hidden">
        <div className="w-52">
          <Label className="caption-label">Staff</Label>
          <Select value={staffId} onValueChange={setStaffId}>
            <SelectTrigger data-testid="commission-staff-select"><SelectValue placeholder="Choose" /></SelectTrigger>
            <SelectContent>
              {staff.map(s => <SelectItem key={s.id} value={s.id} data-testid={`commission-staff-option-${s.working_name}`}>{s.working_name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="caption-label">{t("common.start_date")}</Label>
          <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="commission-start" />
        </div>
        <div>
          <Label className="caption-label">{t("common.end_date")}</Label>
          <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="commission-end" />
        </div>
        <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-3.5 h-3.5 mr-1" />Refresh</Button>
        <Button variant="outline" size="sm" onClick={print} data-testid="commission-print"><Printer className="w-3.5 h-3.5 mr-1" />{t("common.print")}</Button>
        {canEmail && (
          <Button size="sm" className="bg-emerald-800 hover:bg-emerald-900" onClick={emailReport} disabled={busy} data-testid="staff-commission-email-button">
            <Mail className="w-3.5 h-3.5 mr-1" />{t("common.email_report")}
          </Button>
        )}
      </div>
      {data?.staff && (
        <Card className="mb-4 border-stone-200/80">
          <CardContent className="p-4 flex items-center gap-3">
            <User2 className="w-5 h-5 text-emerald-800" />
            <div className="flex-1">
              <div className="font-serif text-lg">{data.staff.working_name}</div>
              <div className="text-xs text-stone-500">{data.staff.name} · {data.staff.email || "no email on file"}</div>
            </div>
            {data.staff.guaranteed_daily_pay_thb > 0 && (
              <div className="text-right">
                <div className="caption-label">{t("reports.col.guaranteed_per_day")}</div>
                <div className="font-mono tabular text-lg text-stone-900">{fmtTHB(data.staff.guaranteed_daily_pay_thb)}</div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      {(() => {
        const showGuar = (data?.staff?.guaranteed_daily_pay_thb || 0) > 0;
        const colCount = showGuar ? 7 : 4;
        return (
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="staff-commission-table">
          <thead className="text-left border-b border-stone-200">
            <tr>
              <th className="py-2 pr-3 caption-label">{t("common.date")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("common.customers")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("common.minutes")}</th>
              <th className="py-2 pr-3 caption-label text-right">{t("common.commission")}</th>
              {showGuar && <th className="py-2 pr-3 caption-label text-right">{t("reports.col.guaranteed")}</th>}
              {showGuar && <th className="py-2 pr-3 caption-label text-right">{t("reports.col.top_up")}</th>}
              {showGuar && <th className="py-2 pr-3 caption-label text-right">{t("reports.col.total_pay")}</th>}
            </tr>
          </thead>
          <tbody>
            {data?.rows.map(r => (
              <tr
                key={r.date}
                className={`border-b border-stone-100 ${showGuar && r.top_up > 0 ? "bg-amber-50/60" : ""}`}
                data-testid={`commission-row-${r.date}`}
              >
                <td className="py-2 pr-3 font-mono">{r.date}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{r.customers}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{r.minutes}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(r.commission)}</td>
                {showGuar && <td className="py-2 pr-3 text-right font-mono tabular text-stone-500">{fmtTHB(r.guaranteed)}</td>}
                {showGuar && (
                  <td
                    className={`py-2 pr-3 text-right font-mono tabular ${r.top_up > 0 ? "text-amber-900 font-medium" : "text-stone-400"}`}
                    data-testid={`commission-topup-${r.date}`}
                  >
                    {fmtTHB(r.top_up)}
                  </td>
                )}
                {showGuar && <td className="py-2 pr-3 text-right font-mono tabular font-medium">{fmtTHB(r.total_pay)}</td>}
              </tr>
            ))}
            {(!data?.rows || data.rows.length === 0) && <tr><td colSpan={colCount} className="py-6 text-center text-stone-500">{t("reports.no_data")}</td></tr>}
          </tbody>
          {data?.rows?.length > 0 && (
            <tfoot className="font-medium border-t-2 border-stone-300 bg-stone-50/60" data-testid="commission-totals-row">
              <tr>
                <td className="py-2 pr-3 text-right">{t("common.total")}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{data.totals.customers}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{data.totals.minutes}</td>
                <td className="py-2 pr-3 text-right font-mono tabular">{fmtTHB(data.totals.commission)}</td>
                {showGuar && <td className="py-2 pr-3 text-right font-mono tabular text-stone-500">—</td>}
                {showGuar && (
                  <td
                    className={`py-2 pr-3 text-right font-mono tabular ${data.totals.top_up > 0 ? "text-amber-900" : "text-stone-400"}`}
                    data-testid="commission-topup-total"
                  >
                    {fmtTHB(data.totals.top_up)}
                  </td>
                )}
                {showGuar && <td className="py-2 pr-3 text-right font-mono tabular" data-testid="commission-total-pay">{fmtTHB(data.totals.total_pay)}</td>}
              </tr>
            </tfoot>
          )}
        </table>
        </div>
      );
      })()}
    </>
  );
}

export default function ShopReports() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { t } = useLang();
  const [shop, setShop] = useState(null);
  const [tab, setTab] = useState("daily");

  useShopIdleLogout(slug, t("shop.idle_signout"));

  useEffect(() => {
    const sess = getShopSession(slug);
    if (!sess?.token) { nav(`/${slug}`); return; }
    // Reports are for owner/manager only per spec — staff PINs cannot enter.
    if (!["owner", "manager"].includes(sess.role)) { nav(`/${slug}`); return; }
    (async () => { try { const r = await axios.get(`${API}/shops/by-slug/${slug}`); setShop(r.data); } catch (e) { /* header renders without shop data */ } })();
  }, [slug, nav]);

  return (
    <div className="min-h-screen shop-bg">
      <ShopHeader shop={shop} showSignOut />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <button onClick={() => nav(`/${slug}`)} className="caption-label flex items-center gap-1 mb-2 text-stone-500 hover:text-stone-800 print:hidden" data-testid="reports-back-link">
          <ChevronLeft className="w-3 h-3" /> back
        </button>
        <h1 className="font-serif text-4xl text-stone-900 mb-6 print:hidden">{t("reports.title")}</h1>
        {!shop?.logo_path && (
          <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 print:hidden" data-testid="reports-logo-missing-banner">
            {t("reports.logo_required")}
          </div>
        )}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="mb-6 bg-stone-100 print:hidden">
            <TabsTrigger value="daily" data-testid="reports-tab-daily" className="font-bold data-[state=active]:bg-emerald-800 data-[state=active]:text-amber-50 data-[state=active]:shadow"><FileText className="w-4 h-4 mr-1.5" />{t("reports.daily")}</TabsTrigger>
            <TabsTrigger value="by-time" data-testid="reports-tab-by-time" className="font-bold data-[state=active]:bg-emerald-800 data-[state=active]:text-amber-50 data-[state=active]:shadow"><Clock className="w-4 h-4 mr-1.5" />{t("reports.customers_by_time")}</TabsTrigger>
            <TabsTrigger value="commission" data-testid="reports-tab-commission" className="font-bold data-[state=active]:bg-emerald-800 data-[state=active]:text-amber-50 data-[state=active]:shadow"><User2 className="w-4 h-4 mr-1.5" />{t("reports.staff_commission")}</TabsTrigger>
            <TabsTrigger value="profit" data-testid="reports-tab-profit" className="font-bold data-[state=active]:bg-emerald-800 data-[state=active]:text-amber-50 data-[state=active]:shadow"><TrendingUp className="w-4 h-4 mr-1.5" />{t("reports.profit")}</TabsTrigger>
            <TabsTrigger value="summary" data-testid="reports-tab-summary" className="font-bold data-[state=active]:bg-emerald-800 data-[state=active]:text-amber-50 data-[state=active]:shadow"><PieChart className="w-4 h-4 mr-1.5" />{t("reports.summary_profit")}</TabsTrigger>
            <TabsTrigger value="income-expenditure" data-testid="reports-tab-ie" className="font-bold data-[state=active]:bg-emerald-800 data-[state=active]:text-amber-50 data-[state=active]:shadow"><Scale className="w-4 h-4 mr-1.5" />{t("reports.income_expenditure")}</TabsTrigger>
          </TabsList>
          <Card className="border-stone-200/80 print:border-0 print:shadow-none">
            <CardContent className="p-6 print:p-0">
              <TabsContent value="daily"><DailyReport slug={slug} shop={shop} /></TabsContent>
              <TabsContent value="by-time"><DailyReport slug={slug} shop={shop} byTime /></TabsContent>
              <TabsContent value="commission"><StaffCommission slug={slug} shop={shop} /></TabsContent>
              <TabsContent value="profit"><ProfitReport slug={slug} shop={shop} /></TabsContent>
              <TabsContent value="summary"><ProfitReport slug={slug} shop={shop} summary /></TabsContent>
              <TabsContent value="income-expenditure"><IncomeExpenditureReport slug={slug} shop={shop} /></TabsContent>
            </CardContent>
          </Card>
        </Tabs>
      </main>
    </div>
  );
}
